import React, { useState } from 'react';
import { AdminLayout } from '@/components/AdminLayout';
import { Link, router } from '@inertiajs/react';
import { Plus, Edit, Trash2, FileText, BookOpen, FileCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';

type Document = {
  id: number;
  key: string;
  title: string;
  content: string;
  document_type: string;
  published: boolean;
  community_section_key?: string | null;
  section_display_name?: string;
};

type Props = {
  documents: Document[];
};

const documentTypeIcons: Record<string, React.ReactNode> = {
  terms_of_agreement: <FileCheck className="h-5 w-5" />,
  handbook: <BookOpen className="h-5 w-5" />,
  guide: <FileText className="h-5 w-5" />,
};

export default function DocumentsIndex({ documents }: Props) {
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const handleDelete = () => {
    if (deleteId) {
      router.delete(`/admin/documents/${deleteId}`, {
        onSuccess: () => setDeleteId(null),
      });
    }
  };

  // Group documents by type
  const groupedDocuments = documents.reduce((acc, doc) => {
    if (!acc[doc.document_type]) {
      acc[doc.document_type] = [];
    }
    acc[doc.document_type].push(doc);
    return acc;
  }, {} as Record<string, Document[]>);

  return (
    <AdminLayout title="Documents">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Documents</h1>
            <p className="mt-1 text-sm text-gray-600">
              Manage terms of agreement, handbooks, and guides
            </p>
          </div>
          <Button asChild>
            <Link href="/admin/documents/new">
              <Plus className="h-4 w-4 mr-2" />
              Add Document
            </Link>
          </Button>
        </div>

        {/* Documents by Type */}
        {Object.entries(groupedDocuments).map(([type, docs]) => (
          <div key={type} className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center gap-2">
                {documentTypeIcons[type] || <FileText className="h-5 w-5" />}
                <h2 className="text-lg font-medium text-gray-900">
                  {type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </h2>
              </div>
            </div>
            <div className="divide-y divide-gray-200">
              {docs.map((document) => (
                <div key={document.id} className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-base font-medium text-gray-900">
                          {document.title}
                        </h3>
                        <Badge variant={document.published ? "default" : "secondary"}>
                          {document.published ? "Published" : "Draft"}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-600 mb-2">
                        <span>
                          Key: <code className="px-2 py-1 bg-gray-100 rounded">{document.key}</code>
                        </span>
                        {document.section_display_name && (
                          <span className="flex items-center">
                            <span className="text-gray-400 mr-1">•</span>
                            Section: <Badge variant="outline" className="ml-1">{document.section_display_name}</Badge>
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 line-clamp-2">
                        {document.content.substring(0, 200)}...
                      </p>
                    </div>
                    <div className="flex gap-2 ml-4">
                      <Button
                        size="sm"
                        variant="outline"
                        asChild
                      >
                        <Link href={`/admin/documents/${document.id}/edit`}>
                          <Edit className="h-3 w-3 mr-1" />
                          Edit
                        </Link>
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => setDeleteId(document.id)}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* Empty State */}
        {documents.length === 0 && (
          <div className="bg-white shadow rounded-lg p-6 text-center">
            <p className="text-gray-500">No documents found. Create your first document to get started.</p>
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Document</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this document? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
}